import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import bizApi from "@/lib/businessApi";
import { useBiz } from "@/lib/businessI18n";
import { Badge, btnGhost, cardCls, formatChf } from "./ui";

export default function Billing() {
  const { t } = useBiz();
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState(null);

  useEffect(() => {
    bizApi.get("/billing/summary").then((r) => setSummary(r.data)).catch(() => setSummary({}));
    bizApi.get("/invoices").then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
  }, []);

  const markPaid = (month) => bizApi.post(`/invoices/${month}/status`, { status: "payee" }).then(() => {
    bizApi.get("/invoices").then((r) => setInvoices(r.data));
  }).catch(() => {});

  const kpis = summary ? [
    [t("billing.spend_month"), formatChf(summary.spend_month)],
    [t("billing.unbilled"), `${summary.unbilled_count} · ${formatChf(summary.unbilled_amount)}`],
    [t("billing.open"), summary.open_invoices ?? 0],
    [t("billing.paid"), summary.paid_invoices ?? 0],
  ] : [];

  return (
    <div data-testid="billing-view">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-ink">{t("billing.title")}</h1>
        {summary && (
          <p className="text-xs text-slate">{t("billing.cycle")} : <span className="font-semibold text-ink">{t(`billing.cycle.${summary.billing_cycle || "monthly"}`)}</span></p>
        )}
      </div>

      {!summary ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-miles" /></div> : (
        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="billing-kpis">
          {kpis.map(([label, value]) => (
            <div key={label} className={`${cardCls} p-4`}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate/70">{label}</p>
              <p className="mt-1 text-lg font-bold text-ink">{value}</p>
            </div>
          ))}
        </div>
      )}

      <section className={`${cardCls} mt-4 p-5`} data-testid="billing-invoices">
        <h2 className="text-sm font-bold text-ink">{t("nav.billing")}</h2>
        {!invoices ? <Loader2 className="mt-3 h-4 w-4 animate-spin text-miles" /> : invoices.length === 0 ? (
          <p className="mt-2 text-xs text-slate">{t("missions.empty")}</p>
        ) : (
          <div className="mt-3 space-y-2">
            {invoices.map((inv) => (
              <div key={inv.month} className="flex items-center gap-4 rounded-xl border border-ink/[0.06] p-3.5" data-testid={`invoice-${inv.month}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-xs font-bold text-ink">{inv.number}</p>
                  <p className="text-xs text-slate">{inv.month} · {inv.trips} missions</p>
                </div>
                <p className="font-mono text-sm font-bold text-ink">{formatChf(inv.amount)}</p>
                <Badge tone={inv.status === "payee" ? "paid" : inv.status === "emise" ? "confirmed" : "pending"}>
                  {inv.status === "payee" ? t("comm.st.paid") : inv.status === "emise" ? t("status.confirmed") : t("status.in_progress")}
                </Badge>
                <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/business/invoices/${inv.month}/pdf`}
                  target="_blank" rel="noreferrer" className={btnGhost} data-testid={`invoice-pdf-${inv.month}`}>
                  <Download className="h-3.5 w-3.5" /> PDF
                </a>
                {inv.status !== "payee" && (
                  <button type="button" onClick={() => markPaid(inv.month)} className={btnGhost} data-testid={`invoice-pay-${inv.month}`}>
                    {t("comm.st.paid")}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
